import * as Tooltip from '@radix-ui/react-tooltip';
import { NavLink, matchPath, useLocation } from 'react-router-dom';
import Icon from '@components/common/Icon';
import { radixTooltipContentClass } from '@components/common/radixTooltipContentClass';
import type { SidebarMenuItem } from './sidebarMenu';

interface SidebarMenuLinkProps {
    item: SidebarMenuItem;
    isOpen: boolean;
}

export function SidebarMenuLink({ item, isOpen }: SidebarMenuLinkProps) {
    const location = useLocation();
    const isActive = Boolean(matchPath({ path: item.path, end: false }, location.pathname));

    const linkClassName =
        [
            'flex items-center outline-none',
            isOpen
                ? 'gap-[8px] 2xl:gap-[10px] px-[10px] 2xl:px-[12px] justify-start mb-[2px] 2xl:mb-[10px] last:mb-0'
                : 'w-full justify-center px-[10px] 2xl:px-[12px] mb-[2px] 2xl:mb-[10px] last:mb-0',
            'py-[7px] 2xl:py-[9px] rounded-[8px] text-[12px] 2xl:text-[14px] font-medium text-[var(--white-color)] transition-colors duration-150 cursor-pointer',
            'hover:bg-[var(--primary-color20)] focus-visible:ring-2 focus-visible:ring-[var(--primary-color)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--dark-color)]',
            isActive ? 'bg-[var(--primary-color20)]' : '',
        ].join(' ');

    const link = (
        <NavLink
            to={item.path}
            className={linkClassName}
            aria-label={isOpen ? undefined : item.name}
        >
            <span className="flex items-center justify-center w-4 h-4 shrink-0">
                <Icon name={item.icon} size={16} alt="" />
            </span>
            {isOpen && <span className="text-overflow whitespace-nowrap">{item.name}</span>}
        </NavLink>
    );

    if (isOpen) {
        return link;
    }

    return (
        <Tooltip.Root>
            <Tooltip.Trigger asChild>{link}</Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content
                    side="right"
                    align="center"
                    sideOffset={8}
                    collisionPadding={8}
                    className={radixTooltipContentClass}
                >
                    {item.name}
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
}
